import React, { FC, useState, forwardRef, useRef, useImperativeHandle } from 'react';
import { Box, FormControlProps } from '@mui/material';

/**
 * Custom extension of FormControl component to show FocusRing only with Tab focus
 */
export const FormControl: FC<FormControlProps> = forwardRef(
  ({ onFocus, onBlur, onPointerDown, className, ...rest }, forwardedRef) => {
    const ref = useRef<HTMLDivElement>(null);
    useImperativeHandle(forwardedRef, () => ref.current);
    // detect events to skip focus ring
    const previousEvent = useRef<string | null>(null);
    // append style with focus ring
    const [focusVisible, setFocusVisible] = useState<boolean>(false);

    return (
      <Box
        ref={ref}
        onPointerDown={(e) => {
          previousEvent.current = 'onPointerDown';
          onPointerDown?.(e);
        }}
        onFocus={(e) => {
          if (!['onFocus', 'onPointerDown'].includes(previousEvent.current)) {
            // if not clicked or repeated event, we will show focus ring (force rerender to update class)
            setFocusVisible(true);
          }
          previousEvent.current = 'onFocus';
          onFocus?.(e);
        }}
        onBlur={(e) => {
          // perform cleanup
          if (
            // skip cleanup as onPointerDown might happen after onFocus
            previousEvent.current !== 'onPointerDown' &&
            // focus is not in opened select
            !ref?.current?.matches?.(':has(.MuiSelect-select[aria-expanded="true"])') &&
            // blur does not come from the opened select and now goes into select
            !e?.relatedTarget?.matches?.('.MuiSelect-select') &&
            // blur does not come from datepicker
            !e?.relatedTarget?.matches?.('[class^="MuiPickers"]') &&
            // blur does not come form timepicker
            !e?.relatedTarget?.matches?.('.MuiMultiSectionDigitalClockSection-item')
          ) {
            // cleanup with blur
            setFocusVisible(false);
            previousEvent.current = 'onBlur';
          } else {
            // act with skip that click just happened
            previousEvent.current = 'onPointerDown';
          }
          onBlur?.(e);
        }}
        className={[
          className,
          // we are utilizing class to support <FormControl component='div' /> fallback to always show focus ring
          focusVisible ? '' : 'Flow-NoFocusRing',
        ]
          .filter(Boolean)
          .join(' ')}
        {...rest}
      />
    );
  },
);
